"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { AppDispatch } from "@/store";
import { updateProfile, ProfileFormData } from "@/store/modules/profile/thunks";
import { useUser } from "../user/useUser";

const profileSchema = Yup.object().shape({
  name: Yup.string().required("Nome é obrigatório"),
  email: Yup.string().email("Email inválido").required("Email é obrigatório"),
  phone: Yup.string().notRequired(),
  cpf: Yup.string().notRequired(),
  password: Yup.string().notRequired(),
  confirmPassword: Yup.string()
    .notRequired()
    .test("passwords-match", "Senhas não coincidem", function (value) {
      if (this.parent.password && value && this.parent.password !== value) {
        return false;
      }
      return true;
    }),
  address: Yup.string().notRequired(),
});

export const useEditProfile = () => {
  const dispatch: AppDispatch = useDispatch();
  const { user } = useUser();
  const { loading, error } = useSelector((state: any) => state.profile);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      phone_whatsapp: user.phone || "",
      cpf: user.cpf || "",
      address: user.address || "",
      password: "",
      confirmPassword: "",
    },
  });

  // Sempre reseta o formulário quando os dados do usuário mudarem
  useEffect(() => {
    reset({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      phone_whatsapp: user.phone || "",
      cpf: user.cpf || "",
      address: user.address || "",
      password: "",
      confirmPassword: "",
    });
  }, [user, reset]);

  const onSubmit = async (data: any) => {
    try {
      // Remove confirmPassword before sending
      const { confirmPassword, ...profileData } = data;

      if (!profileData.password) {
        delete profileData.password;
      } else {
        profileData.password_confirmation = confirmPassword;
      }

      const result = await dispatch(updateProfile(profileData));

      if (updateProfile.fulfilled.match(result)) {
        return { success: true };
      } else {
        return {
          success: false,
          error: (result.payload as any)?.error,
        };
      }
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      return { success: false, error: "Erro inesperado" };
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    reset,
    errors,
    isSubmitting: isSubmitting || loading,
    error,
    user,
    control,
  };
};
